import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { STRINGS } from '@/strings/strings';

/**
 * Extensible status-pill rendered on Library StoryCards to signal an
 * asset failure or follow-up state. Bug #3 Part 1 (2026-05-12) shipped
 * `audio_failed` + `pdf_failed`. Bug #3 Part 2 (2026-05-13) extends
 * with `credit_refunded` — the prop-driven shape made this addition a
 * single label/icon entry plus the union extension.
 *
 * Visual: small inline badge with an icon + short label. Sits in the
 * StoryCard meta row (above the title). Subtle (not alarming) — the
 * goal is informational, not error-state-shouting.
 *
 * `credit_refunded` uses a positive-tone color band (dream-mint) and a
 * checkmark icon to communicate "we made it right" rather than another
 * red/warning signal. Failed-asset pill + refund pill render side-by-
 * side when both apply.
 */

export type StoryStatusPillType = 'audio_failed' | 'pdf_failed' | 'credit_refunded';

interface Props {
  type: StoryStatusPillType;
  className?: string;
}

interface PillConfig {
  label: string;
  className: string;
  Icon: typeof AlertCircle;
}

function configFor(type: StoryStatusPillType): PillConfig {
  const s = STRINGS.library.storyCard.statusPill;
  switch (type) {
    case 'audio_failed':
      return {
        label: s.audioFailed,
        className: 'border-dream-gold/40 bg-dream-gold/12 text-warning-text',
        Icon: AlertCircle,
      };
    case 'pdf_failed':
      return {
        label: s.pdfFailed,
        className: 'border-dream-gold/40 bg-dream-gold/12 text-warning-text',
        Icon: AlertCircle,
      };
    case 'credit_refunded':
      return {
        label: s.creditRefunded,
        className: 'border-dream-mint/60 bg-dream-mint/15 text-dream-navy',
        Icon: CheckCircle2,
      };
  }
}

export function StoryStatusPill({ type, className }: Props) {
  const cfg = configFor(type);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xxs font-medium leading-none',
        cfg.className,
        className,
      )}
      data-testid={`story-status-pill-${type}`}
    >
      <cfg.Icon className="h-3 w-3" strokeWidth={2} aria-hidden="true" />
      <span>{cfg.label}</span>
    </span>
  );
}
